import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';
import OfferteForm from '../../components/feature/OfferteForm';

const WHATSAPP_URL = 'https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20een%20offerte%20aanvragen%20voor%20inkopen%20uit%20China.';

export default function OffertePage() {
  useSEO({
    title: 'Gratis Offerte Aanvragen | Inkopen uit China – DUC Direct Uit China',
    description: 'Vraag een gratis en vrijblijvende offerte aan voor inkopen uit China. Binnen 24 uur een prijsindicatie. Product sourcing, private label en DDP levering door DUC.',
    keywords: 'offerte aanvragen inkopen China, gratis prijscheck China, sourcing agent offerte Nederland, private label offerte China',
    canonical: `${SITE_URL}/offerte`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Offerte aanvragen', item: `${SITE_URL}/offerte` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Gratis Offerte Aanvragen — Inkopen uit China via DUC',
        description: 'Vraag een gratis en vrijblijvende prijsofferte aan voor inkopen uit China. Binnen 24 uur een transparante prijsindicatie van sourcing agent DUC.',
        url: `${SITE_URL}/offerte`,
        inLanguage: 'nl-NL',
        publisher: {
          '@type': 'Organization',
          name: 'Direct Uit China (DUC)',
          url: SITE_URL,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Gratis Offerte Aanvragen — Product Sourcing China',
        description: 'Vraag gratis een offerte aan voor inkopen uit China. DUC regelt product sourcing, private label productie en all-in DDP levering. Binnen 24 uur een heldere prijsindicatie.',
        provider: {
          '@type': 'Organization',
          name: 'Direct Uit China (DUC)',
          url: SITE_URL,
          telephone: '+31624359162',
          email: 'Info@directuitchina.nl',
        },
        serviceType: 'Product Sourcing China',
        areaServed: { '@type': 'Country', name: 'Netherlands' },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'EUR',
          description: 'Gratis en vrijblijvende prijsindicatie binnen 24 uur',
          availability: 'https://schema.org/InStock',
        },
        url: `${SITE_URL}/offerte`,
      },
    ],
  });

  // Haal eventuele prefill-data op vanuit de calculator (via location state)
  const location = useLocation();
  const prefill = (location.state as { productNaam?: string; aantalStuds?: string; totaalprijs?: string } | null) ?? undefined;

  // Scroll naar boven bij laden
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const voordelen = [
    { icon: 'ri-time-line', text: 'Binnen 24 uur een prijsindicatie' },
    { icon: 'ri-checkbox-circle-line', text: 'Gratis en volledig vrijblijvend' },
    { icon: 'ri-shield-check-line', text: 'Geen verplichtingen' },
    { icon: 'ri-truck-line', text: 'All-in DDP prijzen mogelijk' },
    { icon: 'ri-group-line', text: 'Persoonlijk advies inbegrepen' },
    { icon: 'ri-award-line', text: 'Gemiddeld 30–80% goedkoper' },
  ];

  const reviews = [
    { naam: 'Thomas V.', bedrijf: 'Retail NL', tekst: 'Binnen één dag had ik een scherpe offerte. Uiteindelijk 55% goedkoper inkopen dan mijn vorige leverancier.', rating: 5 },
    { naam: 'Naomi K.', bedrijf: 'Private Label Brand', tekst: 'Super makkelijk formulier, duidelijke communicatie en een offerte die echt klopte met wat we zochten.', rating: 5 },
    { naam: 'Daan P.', bedrijf: 'Webshop eigenaar', tekst: 'Shubam reageert snel en denkt actief mee. De offerte die ik ontving was compleet en transparant.', rating: 5 },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Nav */}
      <header className="bg-[#0A1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png"
              alt="DUC - Direct uit China"
              className="h-[70px] w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-[#3B9EE8] text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Terug naar home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0A1628] relative overflow-hidden pb-24 pt-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/75d5e870929ddddb66c852800d78dd51.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-8"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2147]/90 to-[#0A1628]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
          >
            <i className="ri-rocket-line" />
            Gratis &amp; vrijblijvend
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-4xl md:text-5xl font-800 mb-4 leading-tight"
          >
            Vraag gratis een <span className="text-[#3B9EE8]">prijsofferte</span> aan
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-white/60 text-lg max-w-2xl mx-auto"
          >
            Vul het formulier in en ontvang binnen 24 uur een transparante prijsindicatie — inclusief gratis begeleiding en advies van ons team.
          </motion.p>

          {prefill?.productNaam && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 inline-flex items-center gap-2 bg-green-500/15 border border-green-400/30 rounded-xl px-5 py-3"
            >
              <i className="ri-calculator-line text-green-400 text-sm" />
              <span className="text-green-300 text-sm">
                <strong>{prefill.productNaam}</strong> — jouw berekening is al ingevuld
              </span>
            </motion.div>
          )}
        </div>
      </section>

      {/* Main content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Linker info kolom */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              {/* Voordelen */}
              <div>
                <h2 className="text-[#0A1628] font-700 text-xl mb-5">Wat je van ons mag verwachten</h2>
                <div className="flex flex-col gap-3">
                  {voordelen.map((v) => (
                    <div key={v.text} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#0A1628]/8">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#3B9EE8]/10 flex-shrink-0">
                        <i className={`${v.icon} text-[#3B9EE8] text-base`} />
                      </div>
                      <span className="text-[#0A1628] text-sm font-500">{v.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* WhatsApp block */}
              <div className="p-6 bg-[#0A1628] rounded-2xl">
                <p className="text-[#3B9EE8] font-600 text-sm mb-2">Liever direct bellen of appen?</p>
                <p className="text-white/55 text-sm leading-relaxed mb-4">
                  Shubam staat klaar om je te helpen. Stuur een WhatsApp en hij reageert doorgaans binnen enkele uren.
                </p>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BC5A] text-white font-600 text-sm px-5 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base" />
                  App ons nu — +31 6 24 35 91 62
                </a>
              </div>

              {/* Reviews */}
              <div>
                <h3 className="text-[#0A1628] font-700 text-base mb-4">Wat klanten zeggen</h3>
                <div className="flex flex-col gap-4">
                  {reviews.map((r) => (
                    <div key={r.naam} className="bg-white rounded-xl border border-[#0A1628]/8 p-5">
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-[#F7C948] text-sm" />
                        ))}
                      </div>
                      <p className="text-[#0A1628]/70 text-sm leading-relaxed mb-3">&ldquo;{r.tekst}&rdquo;</p>
                      <div>
                        <p className="text-[#0A1628] font-600 text-sm">{r.naam}</p>
                        <p className="text-[#0A1628]/40 text-xs">{r.bedrijf}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Formulier */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white rounded-2xl border border-[#0A1628]/8 p-8 md:p-10"
              >
                <div className="mb-7">
                  <p className="text-[#3B9EE8] text-xs font-600 uppercase tracking-widest mb-1">
                    Ontvang binnen 24 uur een prijsindicatie
                  </p>
                  <h3 className="font-heading font-700 text-[#0A1628] text-2xl">Gratis Prijscheck Aanvragen</h3>
                  <p className="text-[#0A1628]/50 text-sm mt-1.5">Vul de 3 stappen in — duurt minder dan 2 minuten.</p>
                </div>
                <OfferteForm prefill={prefill} />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050E1A] py-8 text-center">
        <p className="text-white/30 text-xs">
          &copy; {new Date().getFullYear()} Direct uit China (DUC). Alle rechten voorbehouden. &nbsp;
          <Link to="/privacybeleid" className="hover:text-white/60 transition-colors">Privacybeleid</Link>
          &nbsp;·&nbsp;
          <Link to="/algemene-voorwaarden" className="hover:text-white/60 transition-colors">Algemene Voorwaarden</Link>
          &nbsp;·&nbsp;
          <Link to="/faq" className="hover:text-white/60 transition-colors">FAQ</Link>
        </p>
      </footer>
    </div>
  );
}
